import { useMemo } from 'react';
import useSimpleTour from './hooks/useSimpleTour';

/**
 * One tour for the three contact sheets — Customers, Brokers and Sellers.
 *
 * They are the same screen three times over: CommonDataView above, SheetFooter
 * below, the same column menu, the same multi-select, the same Add form. Three
 * separate step lists would have been three places to update every time the
 * shared footer grew a control, which is exactly how the live search and the
 * density toggle ended up documented on the Sales tour and nowhere else.
 *
 * They are still three entries in the help menu rather than one, because the
 * menu's job is to name screens: "Customers tour" from the Brokers page has to
 * take you to Customers, and a single merged entry could not say where it goes.
 *
 * `noun` is the plural lower-case name ("customers"), `label` the capitalised
 * one ("Customers"), and `contentId` the screen's own container.
 */
export default function useCrmSheetTour({ noun, label, contentId, canMessage }) {
    const steps = useMemo(() => [
        {
            target: `#${contentId}`,
            title: label,
            content: `Every one of your ${noun} lives on this one sheet. It works the same way as Inventory — and the same way the Brokers and Sellers sheets do — so learning it once covers all of them.`,
            disableBeacon: true,
            placement: 'center',
        },
        {
            target: '#main-table-head',
            title: 'The columns are yours to arrange',
            content: 'Drag a column heading left or right to reorder it, or right-click one to sort, filter, or hide it. The Inventory tour walks through the filter menu in detail.',
            disableBeacon: true,
        },
        {
            target: '#search-bar',
            title: 'Search as you type',
            content: `Type a name or a phone number and the list narrows itself — there is no Go button any more. It searches all your ${noun}, not just the page you happen to be on.`,
            disableBeacon: true,
        },
        {
            target: '#main-table-body tr:first-child',
            title: 'Open a record',
            content: 'Double-click any row to open it, see everything on file, and make changes.',
            disableBeacon: true,
            spotlightClicks: false,
            placement: 'bottom',
        },
        //Only worth explaining to someone who could act on a selection. Without
        //messages.broadcast the tick column still appears, but "Message
        //selected" never does, so the step would describe a button that is not
        //going to be there.
        ...(canMessage ? [
            {
                target: '#select-page-checkbox',
                title: 'Pick out several at once',
                content: `Tick the rows you want — or this box for the whole page — and a "Message selected" button appears at the bottom, which carries them straight into the composer.`,
                disableBeacon: true,
                //The tick column is the leftmost thing on screen and this
                //checkbox is ~13px wide, so a 384px tooltip centred on it starts
                //at about -12, and preventOverflow does not rescue this one the
                //way it rescues the footer steps. 'bottom-start' changed the
                //reported placement and not the rect; 'right' puts the tooltip
                //in the ~1400px of clear space beside it. Measured, not assumed.
                placement: 'right',
            },
            {
                target: '#search-bar',
                title: 'Filter first, then select',
                content: 'The ticks are how you message a few people. To reach everyone, search or filter down to the group you mean, or use Messages → Compose and pick an audience there — the tick boxes only reach the rows on screen.',
                disableBeacon: true,
            },
        ] : []),
        {
            target: '#sheet-add-new',
            title: 'Add a new record',
            content: `Click here to add ${noun === 'customers' ? 'a customer' : noun === 'brokers' ? 'a broker' : 'a seller'} without going through a sale first.`,
            disableBeacon: true,
        },
        {
            target: '#export-open-button',
            title: 'Download the list',
            content: 'Export what you are looking at to Excel. The dialog has its own short tour explaining what "all pages" and "hidden columns" do.',
            disableBeacon: true,
            //Floats against the right edge, so a tooltip below it overhangs the
            //viewport by ~48px. To its left there is a whole screen of room.
            placement: 'left',
        },
        {
            target: '#records-per-page',
            title: 'Rows, roomy or compact',
            content: 'Choose how many records a page holds, and use the button beside it to switch between comfortable and compact rows. Compact fits about half again as many on screen.',
            disableBeacon: true,
        },
        {
            target: '#pagination-controls',
            title: 'Moving through the list',
            content: 'Step through with the arrows or jump straight to a page number. The count to the left tells you how many records there are in total.',
            disableBeacon: true,
        },
    ], [noun, label, contentId, canMessage]);

    return useSimpleTour(steps);
}
